import React, {useEffect, useState} from "react";
import {
    Clock,
    Mail,
    Phone,
    MessageSquare,
    Users,
    Calendar,
    Clipboard,
    CheckCircle,
    AlertTriangle
} from "lucide-react";
import useApi from "../../../utils/useApi";
import {Link, useSearchParams} from "react-router-dom";
import {useNavVisibility} from "../../NavVisibilityContext/NavVisibilityContext";
import {FaFileCircleCheck, FaIdCard} from "react-icons/fa6";
import SelectTaxYear from "../SelectTaxYear/SelectTaxYear";
import {useTaxYear} from "../../../contexts/TaxYearContext";
import {BsCreditCard2FrontFill} from "react-icons/bs";
import {TbCreditCardOff, TbCreditCardPay} from "react-icons/tb";

const DashboardCard = ({ title, counts, icon: Icon, hasNew, textClass, iconClass }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
            {hasNew && counts.new > 0 ? (
                <span className="px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900 rounded-full">
                  NEW
                </span>
            ) : (
                <span className="px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900 rounded-full opacity-0">
                  NEW
                </span>
            )}
        </div>
        <div className="flex items-center space-x-2">
            <Icon className={`w-5 h-5  ${iconClass ? iconClass : "text-blue-500 dark:text-blue-400"}`}/>
            <span className={`text-2xl font-bold  ${textClass ? textClass : 'text-blue-600 dark:text-blue-400'}`}>{counts.total}</span>
        </div>

        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {hasNew ? (
                <>
                    {counts.old > -1 ? (
                        <span>({counts.old} accounts are past due)</span>
                    ) : (

                         <span>({counts.new} new {counts.new > 1 || counts.new <= 0 ? "updates" : "update"})</span>
                     )}
                </>
            ):(
                <span style={{opacity:0}}>(|)</span>
            )}

        </p>
    </div>
);

const CommunicationCard = ({ title, counts, icon: Icon, hasNew }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
                <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400"/>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
            </div>
            {hasNew && counts.new > 0 && (
                <span className="px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900 rounded-full">
          NEW
        </span>
            )}
        </div>

        <div className="flex flex-col">
            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{counts.total}</span>
            {counts.new > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-300">({counts.new} {counts.newLabel ? counts.newLabel : 'new'} this week)</span>
            )}
        </div>
    </div>
);

/**
 * Skeleton components for the loading state
 */

const DashboardCardSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm animate-pulse">
        <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-1/2 rounded"/>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-8 rounded"/>
        </div>
        <div className="flex items-center space-x-2">
            {/* Placeholder for icon */}
            <div className="w-5 h-5 bg-gray-300 dark:bg-gray-700 rounded-full"/>
            <div className="h-6 bg-gray-300 dark:bg-gray-700 w-1/4 rounded"/>
        </div>
        <div className="mt-1 h-4 bg-gray-300 dark:bg-gray-700 w-1/3 rounded"/>
    </div>
);

const CommunicationCardSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm animate-pulse">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-300 dark:bg-gray-700 rounded-full"/>
                <div className="h-4 bg-gray-300 dark:bg-gray-700 w-1/2 rounded"/>
            </div>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-8 rounded"/>
        </div>
        <div className="flex flex-col">
            <div className="h-6 bg-gray-300 dark:bg-gray-700 w-1/4 rounded mb-1"/>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-1/3 rounded"/>
        </div>
    </div>
);

const Dashboard = ({ user, handleShowHeader, handleShowSettingsHeader }) => {
    const { getAdminDashboard } = useApi();
    const { taxYear, updateTaxYear } = useTaxYear();

    const [searchParams, setSearchParams] = useSearchParams();
    const { setIsNavVisible } = useNavVisibility();
    const [availableYears, setAvailableYears] = useState([]);

    const [dashboardData, setDashboardData] = useState({
                                                           readyToFileCount: { total: 0, new: 0 },
                                                           inProgressCount: { total: 0, new: 0 },
                                                           incompleteCount: { total: 0, new: 0 },
                                                           processedCount: { total: 0, new: 0 },
                                                           unreadMessagesCount: { total: 0, new: 0 },
                                                           portalRequestCount: { total: 0, new: 0 },
                                                           identificationCount: { total: 0, new: 0 },
                                                           newClientCount: { total: 0, new: 0 },
                                                           returningClientCount: { total: 0, new: 0 },
                                                           businessCount: { total: 0, new: 0 },
                                                           personalCount: { total: 0, new: 0 },
                                                           paymentFulfilledCount: { total: 0, new: 0 },
                                                           paymentMissingCount: { total: 0, old: 0 },
                                                           portalPaymentMissingCount: { total: 0, old: 0 },
                                                           paymentPastDueCount: { total: 0, old: 0 },
                                                           portalPaymentPastDueCount: { total: 0, old: 0 },
                                                       });

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        handleShowSettingsHeader(false);
        handleShowHeader(true);
        setIsNavVisible(true);
        initialize();
        return () => {
            console.log("Dashboard UNMOUNTED");
        };
    }, [taxYear]);

    const initialize = async () => {
        setIsLoading(true);
        try {
            const response = await getAdminDashboard({ year: taxYear });
            setDashboardData(response);
            setAvailableYears(response.availableYears);
        }
        catch (error) {
            console.error("Error fetching dashboard data:", error);
        }
        setIsLoading(false);
    };

    const getDashboardData = async () => {
        setIsLoading(true);

        const response = await getAdminDashboard({ year: taxYear });
        setDashboardData(response);
        //setAvailableYears(response.availableYears);
        setIsLoading(false);

    }

    const overviewData = [
        {
            title: "Ready To File",
            counts: dashboardData.readyToFileCount,
            icon: FaFileCircleCheck,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=readyToFile&typeLabel=Ready To File`,
            iconClass: 'text-yellow-400',
            textClass: 'text-yellow-400'
        },
        {
            title: "In Progress",
            counts: dashboardData.inProgressCount,
            icon: Clipboard,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=inProgress&typeLabel=In Progress`,
            iconClass: 'text-orange-400',
            textClass: 'text-orange-400'
        },
        {
            title: "Incomplete",
            counts: dashboardData.incompleteCount,
            icon: AlertTriangle,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=incomplete&typeLabel=Incomplete`,
            iconClass: 'text-red-400',
            textClass: 'text-red-400'
        },
        {
            title: "Processed",
            counts: dashboardData.processedCount,
            icon: CheckCircle,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=processed&typeLabel=Processed`,
            iconClass: 'text-green-400',
            textClass: 'text-green-400'
        },
        {
            title: "Portal Requests",
            counts: dashboardData.portalRequestCount,
            icon: Users,
            hasNew: true,
            link: "/portal-requests"
        },
        /*{
            title: "Clients With IDs",
            counts: dashboardData.identificationCount,
            icon: FaIdCard,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=hasIdentification&typeLabel=Has Identification`
        },*/
        {
            title: "Payments Fulfilled",
            counts: dashboardData.paymentFulfilledCount,
            icon: BsCreditCard2FrontFill,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=paymentFulfilled&typeLabel=Payment Fulfilled`,
            iconClass: 'text-green-400',
            textClass: 'text-green-400'
        },
        {
            title: "Awaiting Payment",
            counts: dashboardData.paymentMissingCount,
            icon: TbCreditCardOff,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=paymentMissing&typeLabel=Awaiting Payment`,
            iconClass: 'text-yellow-400',
            textClass: 'text-yellow-400'
        },
        {
            title: "Awaiting Portal Payment",
            counts: dashboardData.portalPaymentMissingCount,
            icon: TbCreditCardOff,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=portalPaymentMissing&typeLabel=Awaiting Payment`,
            iconClass: 'text-yellow-400',
            textClass: 'text-yellow-400'
        },
        {
            title: "Past Due",
            counts: dashboardData.paymentPastDueCount,
            icon: TbCreditCardPay,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=paymentPastDue&typeLabel=Payment Past Due`,
            iconClass: 'text-red-400',
            textClass: 'text-red-400'
        },
        {
            title: "Portal Payment Past Due",
            counts: dashboardData.portalPaymentPastDueCount,
            icon: TbCreditCardPay,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=portalPaymentPastDue&typeLabel=Payment Past Due`,
            iconClass: 'text-red-400',
            textClass: 'text-red-400'
        }
    ];

    const communicationData = [
        {
            title: "Messages",
            counts: dashboardData.unreadMessagesCount,
            icon: MessageSquare,
            hasNew: true,
            link: "/messages"
        },
        {
            title: "New Clients",
            counts: dashboardData.newClientCount,
            icon: Users,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=new&typeLabel=New`
        },
        {
            title: "Returning Clients",
            counts: dashboardData.returningClientCount,
            icon: MessageSquare,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=returning&typeLabel=Returning`
        },
        /*{
            title: "Business Clients",
            counts: dashboardData.businessCount,
            icon: Mail,
            hasNew: true,
            link: `/clients?year=${taxYear}&type=business&typeLabel=Business`
        },
        {
            title: "Personal Clients",
            counts: dashboardData.personalCount,
            icon: Phone,
            hasNew: false,
            link: `/clients?year=${taxYear}&type=personal&typeLabel=Personal`
        }*/
    ];

    function Header() {
        console.log("Header is rendering...");
        return (
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                    Dashboard ({taxYear})
                </h2>
                <div className="flex items-center space-x-2">
                    <label
                        htmlFor="yearSelect"
                        className="text-sm text-gray-600 dark:text-gray-400"
                    >
                        Tax Year:
                    </label>
                    <SelectTaxYear availableYears={availableYears} selectedYear={taxYear}/>
                </div>
            </div>
        );
    }

    // Using React.memo to ensure it won't re-render unnecessarily:
    const MemoHeader = React.memo(Header);

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen page-container relative z-10">
            <div className="max-w-7xl mx-auto">
                {/* Top row (title + year select) */}
                <MemoHeader/>

                {/* Overview section */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                        Overview
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {overviewData.map((item, index) => {
                            // If we're loading, render the skeleton; otherwise, render the actual card
                            if (isLoading) {
                                return <DashboardCardSkeleton key={index}/>;
                            } else {
                                return item.link ? (
                                    <Link to={item.link} key={index} className="cursor-pointer">
                                        <DashboardCard {...item} />
                                    </Link>
                                ) : (
                                           <DashboardCard key={index} {...item} />
                                       );
                            }
                        })}
                    </div>
                </div>

                {/* Office Communications section */}
                <div>
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                        Office Communications
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                        {communicationData.map((item, index) => {
                            if (isLoading) {
                                return <CommunicationCardSkeleton key={index}/>;
                            } else {
                                return item.link ? (
                                    <Link to={item.link} key={index} className="cursor-pointer">
                                        <CommunicationCard {...item} />
                                    </Link>
                                ) : (
                                           <CommunicationCard key={index} {...item} />
                                       );
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
